import React from 'react'
import { Alert, Form, Col, SecondaryLink, spacer } from 'stylist'
import yup from 'yup'
import styled from 'react-emotion'
import ssoProviders from 'config/ssoProviders'
import GoogleButton from 'components/GoogleButton'
import FormLinks from 'components/FormLinks'
import { findPlatform } from 'config/platforms'
import { yupToFormErrors } from 'formik'

const AlertContainer = styled.div`
  margin-bottom: ${spacer(5)};
`

const googleLoginIsValid = values => values.cookiesAccepted
const cookieError = 'Cookie policy must be accepted'
const schema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .email('Email must be a valid email address')
    .required("Email can't be blank"),
  password: yup.string().required("Password can't be blank"),
  cookiesAccepted: yup
    .boolean()
    .test('is-accepted', cookieError, value => value)
})

const LoginForm = ({
  query,
  notify,
  matchedSsoProvider,
  setState,
  authenticate,
  cookiesAccepted,
  setCookieAcceptance,
  highlightAcceptCookiesButton
}) => {
  const platform = findPlatform(query.get('client_id'))

  return (
    <Form
      initialValues={{
        email: query.get('email') || '',
        password: ''
      }}
      validate={(values, props) => {
        const allValues = { ...values, cookiesAccepted }

        return schema
          .validate(allValues, { abortEarly: false })
          .then(() => {
            return {}
          })
          .catch(error => {
            error.errors.find(a => a === cookieError) &&
              highlightAcceptCookiesButton(true)
            throw yupToFormErrors(error)
          })
      }}
      onSubmit={({ email, password }) =>
        authenticate('oauth2-password-grant', {
          email,
          password
        }).catch(({ payload: { error } }) => {
          if (error === 'invalid_grant') {
            return Promise.reject('Email or password is incorrect')
          } else if (error === 'unconfirmed_user') {
            notify('info', 'Account confirmation required')
          } else if (error === 'password_expired') {
            notify('info', 'Your password is expired, please reset.')
          }
        })
      }
    >
      <Col>
        <Form.Title>Sign in</Form.Title>
        <Form.Errors />
        <Form.Group>
          <label htmlFor="email">Email</label>
          <Form.Input
            id="email"
            type="email"
            name="email"
            autoComplete="off"
            onBlur={() =>
              setState({
                redirectToSsoProvider: Boolean(matchedSsoProvider)
              })
            }
            onChange={e =>
              setState({
                matchedSsoProvider: ssoProviders.find(({ match }) =>
                  match.test(e.target.value)
                )
              })
            }
          />
        </Form.Group>
        {!matchedSsoProvider && (
          <Form.Group>
            <label htmlFor="password">Password</label>
            <Form.Input
              id="password"
              type="password"
              name="password"
              autoComplete="off"
            />
          </Form.Group>
        )}
        <Form.Group>
          <Form.Errors name="cookiesAccepted" />
          {matchedSsoProvider && (
            <AlertContainer>
              <Alert
                kind="info"
                message={
                  cookiesAccepted
                    ? `You will be redirected to ${
                        matchedSsoProvider.name
                      } for authentication`
                    : `Please accept cookies to be redirected to ${
                        matchedSsoProvider.name
                      } for authentication`
                }
              />
            </AlertContainer>
          )}
          <Form.SubmitButton block={true}>Submit</Form.SubmitButton>
        </Form.Group>
        <Form.Group>
          <Form.Field
            name="google"
            render={({ setFieldError, form: { values } }) => (
              <GoogleButton
                valid={googleLoginIsValid({ ...values, cookiesAccepted })}
                onError={() => setFieldError('cookiesAccepted', cookieError)}
              />
            )}
          />
        </Form.Group>
        <FormLinks>
          {platform &&
            platform.signupUrl && (
              <SecondaryLink to={platform.signupUrl}>Sign Up</SecondaryLink>
            )}
          <SecondaryLink to="/login/forgot-password">
            Forgot Password
          </SecondaryLink>
        </FormLinks>
      </Col>
    </Form>
  )
}

export default LoginForm



// WEBPACK FOOTER //
// ./src/routes/login/components/LoginForm.js